import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { ConfigContext } from '../../../Context/ConfigContext';
import { FaFileInvoice, FaChevronDown, FaChevronUp } from "react-icons/fa";
import { OverlayLoader } from '../../../Components/Shimmers';
import config from '../../../appConfig';
import { axiosGet } from '../../../Apis/axiosMethods';

const RequestStatementAccount = () => {
    const { t } = useTranslation();
    const { business_id } = useContext(ConfigContext);

    const [statements, setStatements] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeMonth, setActiveMonth] = useState(null);

    // ================= FETCH BUSINESS INFO =================
    const GetBusinessInfo = async () => {
        try {
            setLoading(true);

            const response = await axiosGet(`/Users/GetBusinessInfo`, { business_id });

            if (response?.data?.success) {
                setStatements(response?.data?.accounts__statement || []);
            } else {
                setStatements([]);
            }
        } catch (error) {
            console.error("GetBusinessInfo Error:", error);
            setStatements([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (business_id) {
            GetBusinessInfo();
        }
    }, [business_id]);

    // ================= REMOVE DUPLICATES =================
    const removeDuplicateStatements = (data) => {
        const uniqueMap = new Map();

        data.forEach(item => {
            if (!item?.credit_statement) return;

            const key = item.credit_statement_id || item.credit_statement;

            if (!uniqueMap.has(key)) {
                uniqueMap.set(key, item);
            }
        });

        return Array.from(uniqueMap.values());
    };

    const uniqueStatements = removeDuplicateStatements(statements).sort((a, b) => new Date(b.statement_from) - new Date(a.statement_from));

    // ================= GROUP BY MONTH =================
    const groupStatementsByMonth = (data) => {
        return data.reduce((acc, item) => {
            const date = new Date(item.statement_from);

            if (isNaN(date.getTime())) {
                return acc;
            }

            const monthYear = date.toLocaleDateString('en-US', {
                month: 'long',
                year: 'numeric'
            });

            if (!acc[monthYear]) {
                acc[monthYear] = [];
            }

            acc[monthYear].push(item);

            return acc;
        }, {});
    };

    const groupedStatements = groupStatementsByMonth(uniqueStatements);
    const hasStatements = Object.keys(groupedStatements).length > 0;

    // ================= FORMAT DATE =================
    const formatDate = (date) => {
        if (!date) return "-";

        const parsedDate = new Date(date);

        if (isNaN(parsedDate.getTime())) {
            return "-";
        }

        return parsedDate.toLocaleDateString('en-GB');
    };

    // ================= FORMAT AMOUNT =================
    const formatAmount = (amount) => {
        return Number(amount || 0).toFixed(2);
    };

    return (
        <main style={{ marginTop: "-10px" }}>

            {/* HEADER */}
            <div className="page-header">
                <div className="container text-center">
                    <nav aria-label="breadcrumb" className="breadcrumb-nav">
                        <ol className="breadcrumb justify-content-center">
                            <li className="breadcrumb-item">
                                <NavLink to="/">{t("home")}</NavLink>
                            </li>

                            <li className="breadcrumb-item">
                                <NavLink to="/MyAccount">{t("my_account")}</NavLink>
                            </li>

                            <li className="breadcrumb-item active">
                                Request Statement
                            </li>
                        </ol>
                    </nav>

                    <h1>Request Statement</h1>
                </div>
            </div>

            {/* LOADER */}
            {loading && (
                <center className="my-5">
                    <OverlayLoader />
                </center>
            )}

            {/* CONTENT */}
            {!loading && (
                <div className="container mt-4">
                    <div className="row justify-content-center">
                        <div className="col-md-8">

                            <div className="card shadow-sm">

                                {/* CARD HEADER */}
                                <div className="card-header bg-white text-center">
                                    <h5 className="mb-0">
                                        View Statements Of Account
                                    </h5>
                                </div>

                                {/* CARD BODY */}
                                <div className="card-body">

                                    {/* NO STATEMENTS */}
                                    {!hasStatements && (
                                        <div className="text-center p-4 bg-light rounded">
                                            <FaFileInvoice size={50} className="text-muted mb-3" />

                                            <h6>No Statements Available</h6>

                                            <p className="text-muted mb-0">
                                                Your credit statements will appear here once generated.
                                            </p>
                                        </div>
                                    )}

                                    {/* STATEMENTS */}
                                    {hasStatements && Object.entries(groupedStatements).map(([month, items]) => {

                                        const isOpen = activeMonth === month;

                                        return (
                                            <div key={month} className="mb-3 border rounded">

                                                {/* MONTH HEADER */}
                                                <div
                                                    className="p-3 d-flex justify-content-between align-items-center bg-light"
                                                    style={{ cursor: "pointer" }}
                                                    onClick={() => setActiveMonth(isOpen ? null : month)}
                                                >
                                                    <span className="fw-bold text-primary">
                                                        {month}
                                                    </span>

                                                    {isOpen ? <FaChevronUp /> : <FaChevronDown />}
                                                </div>

                                                {/* MONTH CONTENT */}
                                                {isOpen && (
                                                    <div className="p-3">
                                                        <div className="row">

                                                            {items.map((statement) => {

                                                                const totalAmount = Number(statement.total_amount) || 0;
                                                                const receivedAmount = Number(statement.received_amount) || 0;

                                                                const remainingAmount = Math.max(totalAmount - receivedAmount, 0);

                                                                const advanceAmount = Math.max(receivedAmount - totalAmount, 0);

                                                                const isPaid = statement.statement_status === 1 || remainingAmount === 0;

                                                                return (
                                                                    <div
                                                                        className="col-md-6 mb-3"
                                                                        key={statement.credit_statement_id}
                                                                    >

                                                                        <div
                                                                            className="statement-card rounded p-3 h-100 bg-white"
                                                                            style={{
                                                                                borderLeft: remainingAmount > 0
                                                                                    ? "2px solid #ffc107"
                                                                                    : "2px solid #28a745",
                                                                                borderTop: "1px solid #eee",
                                                                                borderRight: "1px solid #eee",
                                                                                borderBottom: "1px solid #eee"
                                                                            }}
                                                                        >

                                                                            {/* HEADER */}
                                                                            <div className="d-flex justify-content-between align-items-start mb-2">

                                                                                <div>

                                                                                    <h6 className="mb-1 text-primary">
                                                                                        Statement ID: {statement.credit_statement_id}
                                                                                    </h6>

                                                                                    <small className="text-muted d-block">
                                                                                        From: {formatDate(statement.statement_from)}
                                                                                    </small>

                                                                                    <small className="text-muted d-block">
                                                                                        To: {formatDate(statement.statement_to)}
                                                                                    </small>

                                                                                    <small className="text-muted">
                                                                                        Due Date: {formatDate(statement.due_date)}
                                                                                    </small>

                                                                                </div>

                                                                                <span className={`badge ${isPaid ? "bg-success" : "bg-warning"} text-white`}>
                                                                                    {isPaid ? "Paid" : "Unpaid"}
                                                                                </span>

                                                                            </div>

                                                                            {/* AMOUNTS */}
                                                                            <div className="mb-2">

                                                                                <small className="d-block">
                                                                                    <strong>Total Due:</strong>{" "}
                                                                                    {formatAmount(totalAmount)}
                                                                                </small>

                                                                                <small className="d-block text-success">
                                                                                    <strong>Received:</strong>{" "}
                                                                                    {formatAmount(receivedAmount)}
                                                                                </small>

                                                                                {advanceAmount > 0 ? (

                                                                                    <div className="mt-2 p-2 rounded advance-box">
                                                                                        <small className="d-flex justify-content-between align-items-center">
                                                                                            <span className="fw-bold text-primary">
                                                                                                Advance Amount
                                                                                            </span>

                                                                                            <span className="badge bg-primary text-white">
                                                                                                {formatAmount(advanceAmount)}
                                                                                            </span>
                                                                                        </small>
                                                                                    </div>

                                                                                ) : (

                                                                                    <small className={`d-block ${remainingAmount > 0 ? "text-danger" : "text-success"}`}>
                                                                                        <strong>Remaining:</strong>{" "}
                                                                                        {formatAmount(remainingAmount)}
                                                                                    </small>

                                                                                )}

                                                                            </div>

                                                                            {/* STATEMENT FILE */}
                                                                            <button
                                                                                className="btn btn-outline-primary btn-sm w-100"
                                                                                disabled={!statement.credit_statement}
                                                                                onClick={() => {
                                                                                    if (statement.credit_statement) {
                                                                                        window.open(
                                                                                            `${config.statement_api}/public/statements/${statement.credit_statement}`,
                                                                                            "_blank"
                                                                                        );
                                                                                    }
                                                                                }}
                                                                            >
                                                                                {statement.credit_statement
                                                                                    ? "Download Statement"
                                                                                    : "File Not Available"}
                                                                            </button>

                                                                        </div>

                                                                    </div>
                                                                );

                                                            })}

                                                        </div>
                                                    </div>
                                                )}

                                            </div>
                                        );

                                    })}

                                </div>

                            </div>

                        </div>
                    </div>
                </div>
            )}

            <style>
                {`
                    .statement-card {
                        transition: all 0.3s ease;
                    }

                    .statement-card:hover {
                        transform: translateY(-4px);
                        box-shadow: 0 8px 20px rgba(0,0,0,0.08);
                    }

                    .advance-box {
                        background: #e8f7ff;
                        border: 1px solid #b6e0ff;
                    }
                `}
            </style>

        </main>
    );
};

export default RequestStatementAccount;